import React, { useState, useEffect } from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';

const ImpactPageView = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [impactStats, setImpactStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchImpactPage();
  }, []);

  const fetchImpactPage = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { 
        'Authorization': `Bearer ${token}`,
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache'
      };

      const [pageRes, statsRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/impact/page/employee`, { 
          headers,
          cache: 'no-store'
        }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/impact/stats`, { 
          headers,
          cache: 'no-store'
        })
      ]);

      if (pageRes.ok) {
        const pageData = await pageRes.json();
        setPage(pageData);
      }

      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setImpactStats(statsData);
      }
    } catch (error) {
      console.error('Error fetching impact page:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderImpactStats = (layout) => {
    if (!impactStats) return null;
    
    const stats = [
      { label: 'Boxes Reused', value: impactStats.boxes_reused },
      { label: 'Trees Saved', value: impactStats.trees_saved },
      { label: 'Litres Water Saved', value: impactStats.water_saved },
      { label: 'Tonnes CO₂ Saved', value: impactStats.co2_saved },
      { label: 'KwH Energy Saved', value: impactStats.energy_saved }
    ];

    // Use the EXACT same design as dashboard - 2 columns on mobile, 3 on larger screens
    return (
      <div className="bg-gradient-to-br from-emerald-700 to-teal-600 rounded-2xl p-4 sm:p-6 shadow-lg">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
          {stats.map((stat, i) => (
            <div key={i} className="bg-white/10 backdrop-blur-sm rounded-xl p-3 sm:p-4 text-center">
              <p className="text-xl sm:text-2xl font-bold text-white mb-1 break-words">{stat.value}</p>
              <p className="text-teal-100 text-[10px] sm:text-xs leading-tight break-words">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 px-6 py-6 flex items-center justify-center">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  if (!page) {
    return (
      <div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 mb-6">
          <Button onClick={() => navigate('/profile')} variant="ghost" size="sm" className="mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Our Impact to Date</h1>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-8 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Impact Page Not Available</h2>
          <p className="text-gray-600">The impact page has not been published yet.</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 mb-6">
        <Button onClick={() => navigate('/profile')} variant="ghost" size="sm" className="mb-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Our Impact</h1>
          <p className="text-sm text-gray-500 mt-1">{page.title}</p>
        </div>
      </div>

      {/* Render Impact Stats directly without any wrapper boxes */}
      {renderImpactStats('grid')}

      {/* Render Page Sections */}
      {page.sections && page.sections.length > 0 && (
        <div className="mt-6 space-y-4">
          {page.sections.map((section, sIndex) => (
            <div 
              key={section.id || sIndex}
              style={{ 
                backgroundColor: section.backgroundColor || '#ffffff',
                padding: section.padding || '20px'
              }}
              className="rounded-lg"
            >
              <div className={`grid gap-6 ${
                section.columnLayout === 1 ? 'grid-cols-1' :
                section.columnLayout === 2 ? 'md:grid-cols-2' :
                section.columnLayout === 3 ? 'md:grid-cols-3' :
                section.columnLayout === 4 ? 'md:grid-cols-4' :
                'grid-cols-1'
              }`}>
                {section.columns?.map((column, cIndex) => (
                  <div key={column.id || cIndex}>
                    {column.blocks?.map((block, bIndex) => {
                      if (block.type === 'image') {
                        // Skip rendering if image URL is empty
                        if (!block.url || block.url.trim() === '') {
                          return null;
                        }
                        
                        return (
                          <div key={bIndex} className="mb-6">
                            {block.link ? (
                              <a href={block.link} target="_blank" rel="noopener noreferrer">
                                <img 
                                  src={block.url} 
                                  alt={block.alt || ''} 
                                  style={{ width: block.width }} 
                                  className="rounded-lg"
                                />
                              </a>
                            ) : (
                              <img 
                                src={block.url} 
                                alt={block.alt || ''} 
                                style={{ width: block.width }} 
                                className="rounded-lg"
                              />
                            )}
                          </div>
                        );
                      } else if (block.type === 'heading') {
                        const HeadingTag = block.level;
                        return (
                          <HeadingTag 
                            key={bIndex} 
                            style={{ color: block.color }} 
                            className={`mb-4 font-bold ${
                              block.level === 'h1' ? 'text-4xl' :
                              block.level === 'h2' ? 'text-3xl' :
                              'text-2xl'
                            }`}
                          >
                            {block.text}
                          </HeadingTag>
                        );
                      } else if (block.type === 'paragraph') {
                        return (
                          <div 
                            key={bIndex} 
                            className="mb-6 prose max-w-none text-gray-700" 
                            dangerouslySetInnerHTML={{ __html: block.content }} 
                          />
                        );
                      } else if (block.type === 'button') {
                        return (
                          <div key={bIndex} className={`mb-6 text-${block.alignment}`}>
                            <a
                              href={block.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                backgroundColor: block.backgroundColor,
                                color: block.textColor
                              }}
                              className="inline-block px-8 py-3 rounded-lg font-semibold hover:opacity-90 transition-opacity"
                            >
                              {block.text}
                            </a>
                          </div>
                        );
                      } else if (block.type === 'imageWithText') {
                        const ImageContent = block.link ? (
                          <a href={block.link} target="_blank" rel="noopener noreferrer">
                            <img 
                              src={block.url} 
                              alt={block.alt || ''} 
                              className="rounded-lg w-full h-auto"
                            />
                          </a>
                        ) : (
                          <img 
                            src={block.url} 
                            alt={block.alt || ''} 
                            className="rounded-lg w-full h-auto"
                          />
                        );

                        return (
                          <div key={bIndex} className={`mb-6 grid gap-6 ${block.imagePosition === 'right' ? 'md:grid-cols-2' : 'md:grid-cols-2'}`}>
                            {block.imagePosition === 'left' ? (
                              <>
                                <div>{ImageContent}</div>
                                <div className="prose max-w-none" dangerouslySetInnerHTML={{ __html: block.content }} />
                              </>
                            ) : (
                              <>
                                <div className="prose max-w-none" dangerouslySetInnerHTML={{ __html: block.content }} />
                                <div>{ImageContent}</div>
                              </>
                            )}
                          </div>
                        );
                      } else if (block.type === 'accordion') {
                        return (
                          <div key={bIndex} className="mb-4">
                            <details className="group">
                              <summary 
                                style={{
                                  backgroundColor: block.titleBgColor || '#00b48f',
                                  color: block.titleColor || '#ffffff'
                                }}
                                className="px-6 py-4 rounded-lg cursor-pointer font-semibold list-none flex items-center justify-between"
                              >
                                <span>{block.title}</span>
                                <span className="transition group-open:rotate-180">▼</span>
                              </summary>
                              <div 
                                style={{
                                  backgroundColor: block.contentBgColor || '#f3f4f6',
                                  color: block.contentColor || '#374151'
                                }}
                                className="px-6 py-4 mt-2 rounded-lg prose max-w-none"
                                dangerouslySetInnerHTML={{ __html: block.content }}
                              />
                            </details>
                          </div>
                        );
                      }
                      return null;
                    })}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ImpactPageView;
